import { Box, Button, Link, Skeleton, VStack } from '@chakra-ui/react';
import React, { useState, useCallback, useMemo, useEffect } from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  BorrowingBasePipelineSingleProgressBreakdownGQLType,
  StepStatusEnumGQLType,
  UrlAndFilenameGQLType,
} from 'types/dashboard-core-gqlTypes.generated';
import { DataTape } from 'types/manualDataTapeUpload';

import { extractDataTapeMapFromRequiredDataTapes } from './extractDataTapeMapFromRequiredDataTapes';
import { VerificationStatusIcon } from './VerificationIcons';
import { VerificationProgressTracker } from './VerificationProgressTracker';
import { VerificationsAccordion } from './VerificationsTemplate';
import { VerificationUploadAccordionPanel } from './VerificationUploadAccordionPanel';
import { colors } from 'packages/common-frontend/colors';
import { InfoIconButton } from 'packages/common-frontend/components/IconButton';
import { useGetCreditFacilityQuery } from 'packages/credit-facility/requests/requests.gql.generated';
import { useGetRequiredDataTapesForVerificationQuery } from 'packages/manualDataTapeUploads/requests/requests.gql.generated';
import { DataTapeFormatModal } from 'packages/manualDataTapeUploads/ui/DataTapeFormatModal';
import { FiDownload } from 'react-icons/fi';

const createProgressBreakdown = ({
  uploadStatus,
  uploadProgressBreakdown,
}: {
  uploadStatus: StepStatusEnumGQLType;
  uploadProgressBreakdown: BorrowingBasePipelineSingleProgressBreakdownGQLType[];
}): BorrowingBasePipelineSingleProgressBreakdownGQLType[] => {
  return [
    {
      stepDescription: 'Upload servicer report',
      status: { status: uploadStatus },
    },
    ...uploadProgressBreakdown,
  ];
};

const DownloadButton: React.FC<{
  download: UrlAndFilenameGQLType;
  servicerReportTape: DataTape | null;
}> = ({ download, servicerReportTape }) => {
  const [isFormatModalOpen, setIsModalFormatOpen] = useState(false);
  return (
    <Box>
      <Link href={download.url} download={download.filename}>
        <Button
          color={colors.aurora[400]}
          borderColor={colors.aurora[400]}
          borderWidth="2px"
          bg={colors.white}
        >
          Download {servicerReportTape ? 'servicer report' : download.filename}
          <Box ml="12px">
            <FiDownload />
          </Box>
        </Button>
      </Link>
      {servicerReportTape && (
        <>
          <DataTapeFormatModal
            isOpen={isFormatModalOpen}
            onClose={() => setIsModalFormatOpen(false)}
            dataTape={servicerReportTape}
          />
          <InfoIconButton
            ariaLabel="Data Tape Format"
            label="Data Tape Format"
            onClick={() => setIsModalFormatOpen(true)}
            fontSize="xl"
          />
        </>
      )}
    </Box>
  );
};

export const VerificationUploadAccordion: React.FC<{
  isExpanded: boolean;
  selectedCreditFacilityId: string;
  selectedReportingDate: Util.FullDateStr;
  uploadIsComplete: boolean;
  onUploadComplete: (
    artifactDocumentId: string,
    creditFacilityId: string,
    reportingDate: Util.FullDateStr,
    fileVersion: number,
  ) => void;
  uploadProgressBreakdown: BorrowingBasePipelineSingleProgressBreakdownGQLType[];
  servicerReportDownload: UrlAndFilenameGQLType | null;
}> = ({
  isExpanded,
  selectedCreditFacilityId,
  selectedReportingDate,
  uploadIsComplete,
  onUploadComplete,
  uploadProgressBreakdown,
  servicerReportDownload,
}) => {
  const [uploadStatus, setUploadStatus] = useState<StepStatusEnumGQLType>(
    'notStarted',
  );
  useEffect(() => {
    if (uploadIsComplete) {
      setUploadStatus('success');
    }
  }, [uploadIsComplete]);

  const onUploadStart = useCallback(() => {
    setUploadStatus('inProgress');
  }, [setUploadStatus]);

  const fullProgressBreakdown = useMemo(
    () =>
      createProgressBreakdown({
        uploadStatus,
        uploadProgressBreakdown,
      }),
    [uploadStatus, uploadProgressBreakdown],
  );

  const { data: creditFacilityData } = useGetCreditFacilityQuery({
    variables: { creditFacilityId: selectedCreditFacilityId },
  });

  const {
    data: requiredDataTapesData,
  } = useGetRequiredDataTapesForVerificationQuery({
    variables: {
      creditFacilityId: selectedCreditFacilityId,
    },
  });

  if (!creditFacilityData || !requiredDataTapesData) {
    return (
      <Skeleton
        data-testid="loading-verification-upload-accordian"
        height="600px"
      />
    );
  }

  const {
    creditFacility: { id, slug, formattedSlug, loanProducts },
  } = creditFacilityData;

  const {
    dataTapes: extractedDataTapes,
  } = extractDataTapeMapFromRequiredDataTapes(requiredDataTapesData);
  // For now, there is always exactly one tape to upload, and hence exactly one uploaded tape that
  // can be retrieved for download.
  const servicerReportTape =
    extractedDataTapes.length === 1 ? extractedDataTapes[0] : null;

  const header = `Upload ${commonDate.fullDateStrToSlashedDateStr(
    selectedReportingDate,
    commonDate.formatSlashedDateStr,
  )} Servicer Report for ${formattedSlug}`;

  return (
    <VerificationsAccordion
      header={header}
      icon={<VerificationStatusIcon status={uploadStatus} />}
      isExpanded={isExpanded}
    >
      {(() => {
        if (uploadStatus === 'notStarted') {
          return (
            <VerificationUploadAccordionPanel
              selectedCreditFacility={{ id, slug, formattedSlug, loanProducts }}
              onUploadStart={onUploadStart}
              onUploadComplete={(
                artifactDocumentId,
                creditFacilityId,
                reportingDate,
                fileVersion,
              ) => {
                setUploadStatus('success');
                onUploadComplete(
                  artifactDocumentId,
                  creditFacilityId,
                  reportingDate,
                  fileVersion,
                );
              }}
              selectedReportingDate={selectedReportingDate}
            />
          );
        }
        return (
          <Box>
            <VStack float="right">
              {servicerReportDownload && (
                <DownloadButton
                  download={servicerReportDownload}
                  servicerReportTape={servicerReportTape}
                />
              )}
            </VStack>
            <VerificationProgressTracker
              progressBreakdown={fullProgressBreakdown}
            />
          </Box>
        );
      })()}
    </VerificationsAccordion>
  );
};
